/* .lbl and .field from the storyboard helmet. */
.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

/* The input, then its help and its error, under the label. */
.body {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

/* Fields side by side (APP-136). A row's class composes `field-row` from global, and every field
   in it shares the row's two tracks: the labels, then the inputs with their help. A label that
   wraps to two lines moves the whole row's inputs down with it, and help under one input leaves
   the others where they are. Anything else in the row (a person picker, a card) spans both
   tracks and keeps its own alignment. Without subgrid the fields line up by their labels, as
   they did before. */
@supports (grid-template-rows: subgrid) {
  :global(.field-row) > * {
    grid-row: span 2;
  }

  :global(.field-row) > .field {
    display: grid;
    grid-template-rows: subgrid;
    row-gap: 8px;
  }

  /* The input's track, even when the label is for screen readers only. */
  :global(.field-row) > .field > .body {
    grid-row: 2;
  }
}

.label {
  color: var(--text-heading);
  font-size: 14px;
  font-weight: var(--fw-semibold);
}

.required {
  margin-left: 2px;
  color: var(--status-problem-ink);
}

.input {
  width: 100%;
  min-height: var(--control-height);
  padding: 0 12px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
  color: var(--text-body);
  font: inherit;
  font-size: 15px;
}

.textarea {
  min-height: 96px;
  padding: 10px 12px;
  line-height: 1.5;
  resize: vertical;
}

.input[data-focused],
.input:focus {
  outline: none;
  border-color: var(--brand-cyan);
  box-shadow: var(--focus-halo);
}

.input[data-invalid] {
  border-color: var(--status-problem-ink);
}

.input[data-disabled] {
  background: var(--gray-50);
  color: var(--text-muted);
}

.description {
  color: var(--text-muted);
  font-size: 12px;
  line-height: 1.45;
}

.error {
  color: var(--status-problem-ink);
  font-size: 13px;
  font-weight: var(--fw-semibold);
}

/* Select */
.selectButton {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  min-height: var(--control-height);
  padding: 0 10px 0 12px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-sm);
  background: var(--white);
  color: var(--text-body);
  font-size: 15px;
  text-align: left;
  cursor: pointer;
}

.selectButton[data-focus-visible] {
  outline: none;
  border-color: var(--brand-cyan);
  box-shadow: var(--focus-halo);
}

.selectValue[data-placeholder] {
  color: var(--text-muted);
}

.popover {
  min-width: var(--trigger-width);
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-md);
  background: var(--white);
  box-shadow: var(--shadow-menu);
}

.listbox {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px;
  outline: none;
}

.option {
  display: flex;
  align-items: center;
  min-height: var(--control-height);
  padding: 0 12px;
  border-radius: var(--radius-sm);
  color: var(--text-body);
  font-size: 14px;
  cursor: pointer;
  outline: none;
}

.option[data-focused],
.option[data-hovered] {
  background: var(--brand-cyan-50);
  color: var(--brand-navy);
}

.option[data-disabled] {
  color: var(--text-muted);
  cursor: not-allowed;
  background: transparent;
}

.option[data-selected] {
  font-weight: var(--fw-semibold);
  color: var(--brand-navy);
}

.option[data-selected]::after {
  content: '✓';
  margin-left: auto;
  color: var(--brand-petrol);
}

/* Switch (role="switch") */
.switchField {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.switch {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  min-height: var(--control-height);
  color: var(--text-heading);
  font-size: 14px;
  font-weight: var(--fw-semibold);
  cursor: pointer;
}

.track {
  position: relative;
  flex: none;
  width: 48px;
  height: 28px;
  border-radius: var(--radius-pill);
  background: var(--switch-off);
  transition: background-color var(--dur-fast) ease;
}

.thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--white);
  box-shadow: 0 1px 2px var(--navy-a35);
  transition: transform var(--dur-fast) ease;
}

.switch[data-selected] .track {
  background: var(--switch-on);
}

.switch[data-selected] .thumb {
  transform: translateX(20px);
}

.switch[data-focus-visible] .track {
  outline: 2px solid var(--focus-ring-color);
  outline-offset: 2px;
}

.switch[data-disabled] {
  cursor: not-allowed;
  opacity: 0.55;
}

.stateWord {
  color: var(--text-secondary);
  font-weight: var(--fw-regular);
}
